import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useLocation } from '../contexts/LocationContext';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import { createTask, getTask, updateTask } from '../utils/api';
import { TaskCreate, TaskUpdate, TaskPriority, TaskCategory } from '../types/api';
import { getErrorMessage } from '../utils/api';
import apiClient from '../utils/api';

const TaskFormPage: React.FC = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const { selectedLocation } = useLocation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [managers, setManagers] = useState<Array<{ id: string; full_name: string }>>([]);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    location_id: selectedLocation?.id || '',
    assigned_to_manager_id: '',
    priority: 'medium' as TaskPriority,
    category: 'general' as TaskCategory,
    due_date: '',
    related_entity_type: '',
    related_entity_id: '',
  });

  const isEdit = !!taskId;

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/tasks');
      return;
    }

    if (selectedLocation?.id) {
      setFormData(prev => ({ ...prev, location_id: selectedLocation.id }));
      loadManagers();
    }

    if (isEdit && taskId) {
      loadTask();
    }
  }, [selectedLocation?.id, taskId, isEdit]);

  const loadManagers = async () => {
    try {
      const response = await apiClient.get('/users');
      const allUsers = response.data || [];
      const managerUsers = allUsers.filter((u: any) => u.role === 'manager' && u.is_active);
      
      // If current user is admin, add them to the list so they can assign to themselves
      if (user?.role === 'admin' && user.id) {
        const adminUser = {
          id: user.id,
          full_name: `${user.full_name} (You)`
        };
        // Add admin at the beginning of the list
        setManagers([adminUser, ...managerUsers]);
      } else {
        setManagers(managerUsers);
      }
    } catch (err: any) {
      console.error('Error loading managers:', err);
    }
  };

  const loadTask = async () => {
    if (!taskId) return;
    try {
      setLoading(true);
      const task = await getTask(taskId);
      setFormData({
        title: task.title,
        description: task.description || '',
        location_id: task.location_id,
        assigned_to_manager_id: task.assigned_to_manager_id,
        priority: task.priority,
        category: task.category,
        due_date: task.due_date || '',
        related_entity_type: task.related_entity_type || '',
        related_entity_id: task.related_entity_id || '',
      });
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to load task'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }

    if (!formData.location_id) {
      setError('Location is required');
      return;
    }

    if (!formData.assigned_to_manager_id) {
      setError('Manager assignment is required');
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      if (isEdit && taskId) {
        const updateData: TaskUpdate = {
          title: formData.title,
          description: formData.description || undefined,
          priority: formData.priority,
          category: formData.category,
          due_date: formData.due_date || undefined,
        };
        await updateTask(taskId, updateData);
      } else {
      const createData: TaskCreate = {
        title: formData.title,
        description: formData.description || undefined,
        location_id: formData.location_id,
        assigned_to_manager_id: formData.assigned_to_manager_id,
        priority: formData.priority,
        category: formData.category,
        due_date: formData.due_date || undefined,
        assignment_type: 'manual',
        related_entity_type: formData.related_entity_type || undefined,
        related_entity_id: formData.related_entity_id || undefined,
      };
        await createTask(createData);
      }

      navigate('/tasks');
    } catch (err: any) {
      setError(getErrorMessage(err, isEdit ? 'Failed to update task' : 'Failed to create task'));
    } finally {
      setSubmitting(false);
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">Admin access required</p>
        <Link to="/tasks" className="text-blue-600 hover:text-blue-800">Back to Tasks</Link>
      </div>
    );
  }

  if (!selectedLocation) {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">Please select a location first</p>
        <Link to="/locations" className="text-blue-600 hover:text-blue-800">Go to Location Selection</Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container-mobile py-8">
        <div className="flex justify-center">
          <LoadingSpinner size="md" />
        </div>
      </div>
    );
  }

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/tasks')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeftIcon className="h-6 w-6" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEdit ? 'Edit Task' : 'Create Task'}
        </h1>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Location */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Location <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={selectedLocation?.name || ''}
            disabled
            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
          />
        </div>

        {/* Assigned To */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Assign To <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.assigned_to_manager_id}
            onChange={(e) => setFormData({ ...formData, assigned_to_manager_id: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
          >
            <option value="">Select Assignee</option>
            {managers.map((manager) => (
              <option key={manager.id} value={manager.id}>
                {manager.full_name}
              </option>
            ))}
          </select>
        </div>

        {/* Priority */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
          <select
            value={formData.priority}
            onChange={(e) => setFormData({ ...formData, priority: e.target.value as TaskPriority })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </div>

        {/* Category */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
          <select
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value as TaskCategory })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="general">General</option>
            <option value="health">Health</option>
            <option value="maintenance">Maintenance</option>
            <option value="data_entry">Data Entry</option>
            <option value="breeding">Breeding</option>
            <option value="harvest">Harvest</option>
          </select>
        </div>

        {/* Due Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
          <input
            type="date"
            value={formData.due_date}
            onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Related Entity (Optional) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Related Entity Type</label>
            <select
              value={formData.related_entity_type}
              onChange={(e) => setFormData({ ...formData, related_entity_type: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">None</option>
              <option value="goat">Goat</option>
              <option value="fish">Fish</option>
              <option value="cycle">Cycle</option>
              <option value="pond_schedule">Pond Schedule</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Related Entity ID</label>
            <input
              type="text"
              value={formData.related_entity_id}
              onChange={(e) => setFormData({ ...formData, related_entity_id: e.target.value })}
              placeholder="Optional: Entity ID"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="flex gap-4 pt-4 border-t border-gray-200">
          <button
            type="submit"
            disabled={submitting}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {submitting ? (isEdit ? 'Updating...' : 'Creating...') : (isEdit ? 'Update Task' : 'Create Task')}
          </button>
          <button
            type="button"
            onClick={() => navigate('/tasks')}
            className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default TaskFormPage;

